<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const props = defineProps({
  slot: {
    type: Object,
    required: true,
  },
  // State from the test conversation: 'concluded' | 'asked' | 'not_concluded' | 'deferred' | null
  // null means no active test conversation — badge is hidden
  slotState: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete']);

// Derived state helpers (only meaningful when slotState is not null)
const isTestActive  = computed(() => props.slotState !== null);
const isConcluded   = computed(() => props.slotState === 'concluded');
const isAsked       = computed(() => props.slotState === 'asked');

// Tooltip for the test-conversation badge
const completionTooltip = computed(() => {
  if (isConcluded.value)  return t('OMNI.SLOTS.STATUS_COMPLETED');
  if (isAsked.value)      return t('OMNI.SLOTS.STATUS_ASKED');
  return t('OMNI.SLOTS.STATUS_PENDING');
});

// Only show ask_question-specific fields when action_type is ask_question
const isAskQuestion = computed(() => (props.slot.action_type || 'ask_question') === 'ask_question');

// Restriction status helpers (for compilation badges)
const hasRestriction = computed(() => !!props.slot.restriction);
const restrictionStatus = computed(() => props.slot.restriction_status || 'none');
const isCompiling = computed(() => ['pending', 'compiling'].includes(restrictionStatus.value));
const isValidated = computed(() => props.slot.restriction_validated);
const isAmbiguous = computed(() => props.slot.restriction_ambiguous);
const isFailed = computed(() => restrictionStatus.value === 'error');
const confidence = computed(() => Math.round((props.slot.restriction_confidence || 0) * 100));

// Action type icon mapping
const actionTypeIcon = computed(() => {
  const type = props.slot.action_type || 'ask_question';
  const icons = {
    ask_question: 'i-lucide-message-circle',
    check_calendar: 'i-lucide-calendar',
    decision: 'i-lucide-brain',
    query_database: 'i-lucide-database',
    send_file: 'i-lucide-paperclip',
    free: 'i-lucide-infinity',
    loop: 'i-lucide-repeat',
  };
  return icons[type] || icons.ask_question;
});

const actionTypeLabel = computed(() => {
  const type = props.slot.action_type || 'ask_question';
  const labels = {
    ask_question: t('OMNI.SLOTS.ACTION_TYPES.ASK_QUESTION'),
    check_calendar: t('OMNI.SLOTS.ACTION_TYPES.CHECK_CALENDAR'),
    decision: t('OMNI.SLOTS.ACTION_TYPES.DECISION'),
    query_database: t('OMNI.SLOTS.ACTION_TYPES.QUERY_DATABASE'),
    send_file: t('OMNI.SLOTS.ACTION_TYPES.SEND_FILE'),
    free: t('OMNI.SLOTS.ACTION_TYPES.FREE'),
    loop: t('OMNI.SLOTS.ACTION_TYPES.LOOP'),
  };
  return labels[type] || labels.ask_question;
});

// Tooltip for restriction status
const restrictionTooltip = computed(() => {
  const notes = props.slot.restriction_notes;
  const errors = props.slot.restriction_errors || [];

  if (isCompiling.value) return t('OMNI.RESTRICTION_STATUS.COMPILING');
  if (isValidated.value) return `${t('OMNI.RESTRICTION_STATUS.VALIDATED')} (${confidence.value}%)`;
  if (isAmbiguous.value) return notes || t('OMNI.RESTRICTION_STATUS.AMBIGUOUS');
  if (isFailed.value) return errors.join(', ') || t('OMNI.RESTRICTION_STATUS.ERROR');

  return '';
});
</script>

<template>
  <div
    class="group relative p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-woot-500 dark:hover:border-woot-500 transition-colors bg-white dark:bg-slate-800"
  >
    <div class="flex items-start justify-between">
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-2">
          <span
            :class="actionTypeIcon"
            class="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-slate-500"
            :title="actionTypeLabel"
          />
          <h3 class="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
            {{ slot.slot_name }}
          </h3>
        </div>

        <p
          v-if="slot.slot_description"
          class="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 mb-2"
        >
          {{ slot.slot_description }}
        </p>

        <div class="flex flex-wrap gap-2">
          <!-- Restriction Status Badges -->
          <!-- Ordem: Compiling > Error > Validated > Ambiguous -->
          <span
            v-if="hasRestriction && isCompiling"
            class="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded"
            :title="restrictionTooltip"
          >
            <span class="i-lucide-loader-2 w-3 h-3 animate-spin" />
            {{ t('OMNI.RESTRICTION_STATUS.COMPILING_SHORT') }}
          </span>

          <span
            v-else-if="hasRestriction && isFailed"
            class="inline-flex items-center gap-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded cursor-help"
            :title="restrictionTooltip"
          >
            <span class="i-lucide-x w-3 h-3" />
            {{ t('OMNI.RESTRICTION_STATUS.ERROR_SHORT') }}
          </span>

          <span
            v-else-if="hasRestriction && isValidated"
            class="inline-flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded"
            :title="restrictionTooltip"
          >
            <span class="i-lucide-check w-3 h-3" />
            {{ t('OMNI.RESTRICTION_STATUS.VALIDATED_SHORT') }}
          </span>

          <span
            v-else-if="hasRestriction && isAmbiguous"
            class="inline-flex items-center gap-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-0.5 rounded cursor-help"
            :title="restrictionTooltip"
          >
            <span class="i-lucide-alert-triangle w-3 h-3" />
            {{ t('OMNI.RESTRICTION_STATUS.AMBIGUOUS_SHORT') }}
          </span>
        </div>

        <p
          v-if="isAskQuestion && slot.question"
          class="mt-2 text-xs text-slate-500 dark:text-slate-400 italic line-clamp-2"
        >
          "{{ slot.question }}"
        </p>
      </div>

      <div class="flex items-start gap-2 ml-2">
        <!-- Priority Badge -->
        <div
          v-if="slot.priority"
          class="w-7 h-7 flex items-center justify-center bg-woot-500 text-white text-xs font-bold rounded"
          :title="t('OMNI.SLOTS.PRIORITY')"
        >
          {{ slot.priority }}
        </div>

        <!-- Action Buttons -->
        <div
          v-if="canEditSection('stages')"
          class="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <button
            class="p-1.5 text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded"
            :title="t('OMNI.COMMON.EDIT')"
            @click="emit('edit', slot)"
          >
            <span class="i-lucide-edit w-4 h-4" />
          </button>
          <button
            class="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            :title="t('OMNI.COMMON.DELETE')"
            @click="emit('delete', slot)"
          >
            <span class="i-lucide-trash w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Test-conversation State Badge (Bottom Right Corner) — hidden when no test conv -->
    <div
      v-if="isTestActive"
      class="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold shadow-md cursor-default transition-all"
      :style="{
        backgroundColor: isConcluded ? '#10b981' : isAsked ? '#f97316' : '#ef4444',
        color: '#ffffff'
      }"
      :title="completionTooltip"
    >
      <Icon
        :icon="isConcluded ? 'i-lucide-check-circle-2' : isAsked ? 'i-lucide-message-square' : 'i-lucide-x-circle'"
        class="size-3"
      />
      <span>{{
        isConcluded
          ? t('OMNI.SLOTS.COMPLETED')
          : isAsked
          ? t('OMNI.SLOTS.ASKED')
          : t('OMNI.SLOTS.PENDING')
      }}</span>
    </div>
  </div>
</template>
